import { DatePicker, Form, FormInstance, Input, Select, Divider } from 'antd';
import * as Component from 'components';
import {
  AGE_RATING,
  DEFAULT_PRODUCE_COUNTRY,
  PRODUCE_COUNTRIES,
  SERIES_DUBBING_MODE,
  STATUS,
} from 'constants/index';
import { SeriesInterface } from 'content/type/seriesType';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { peopleApiService } from 'service/api/peopleApiService';
import { PeopleInterface } from 'type/peopleType';
import { I18nFormFields } from '../../../../../shared/components/i18n/I18nFormFields';
import { I18nFieldData } from '../../../../../shared/types/i18n/i18nTypes';
import { EnhancedCastEditor } from '../../../../../shared/components/enhanced-cast';
import detailHandlers from './detailHandlers';

interface SeriesDetailProps {
  form: FormInstance;
  series?: SeriesInterface;
  actionTitle: string;
  readonly?: boolean;
}

const SeriesDetail: React.FC<SeriesDetailProps> = ({
  form,
  series,
  actionTitle,
  readonly = false,
}) => {
  const [peopleList, setPeopleList] = React.useState<PeopleInterface[]>([]);
  const [selectedPeoples, setSelectedPeoples] = React.useState<number[]>([]);
  const [initialPeopleList, setInitialPeopleList] = React.useState<PeopleInterface[]>([]);

  // Enhanced Cast 상태 관리
  const [enhancedCast, setEnhancedCast] = useState<{ cast: number; type: string }[]>([]);
  const [searchedPeopleList, setSearchedPeopleList] = useState<PeopleInterface[]>([]);

  // i18n 필드 상태 관리
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSynopsis, setI18nSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoTitle, setI18nSeoTitle] = useState<I18nFieldData>({});
  const [i18nSeoSynopsis, setI18nSeoSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoKeyword, setI18nSeoKeyword] = useState<I18nFieldData>({});

  const navigate = useNavigate();
  const { handleOnClickAddButtonClick, handleOnClickEditButtonClick } = detailHandlers({
    navigate,
    form,
    selectedCategories: [],
    setIsModalVisible: () => {},
  });

  // i18n 데이터를 포함한 커스텀 핸들러
  const handleOnClickButtonClickWithI18n = async () => {
    try {
      await form.validateFields();

      // 폼 데이터에 i18n 필드 및 enhanced_cast 추가
      const formData = form.getFieldsValue();
      const seriesDataWithI18n = {
        ...formData,
        i18n_title: i18nTitle,
        i18n_synopsis: i18nSynopsis,
        i18n_seo_title: i18nSeoTitle,
        i18n_seo_synopsis: i18nSeoSynopsis,
        i18n_seo_keyword: i18nSeoKeyword,
        enhanced_cast: enhancedCast,
      };

      if (!seriesDataWithI18n.title) {
        seriesDataWithI18n.title = seriesDataWithI18n.i18n_title?.en || ' ';
      }

      console.log('seriesDataWithI18n', seriesDataWithI18n)

      // 폼에 i18n 데이터 설정
      form.setFieldsValue(seriesDataWithI18n);

      // 기존 핸들러 호출
      if (series) {
        await handleOnClickEditButtonClick();
      } else {
        await handleOnClickAddButtonClick();
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  useEffect(() => {
    const init = async (): Promise<void> => {
      if (!series) {
        return;
      }

      // i18n 필드 초기화
      if (series.i18n_title) {
        setI18nTitle(series.i18n_title);
      } else {
        // 레거시 필드에서 i18n 필드로 변환
        setI18nTitle({
          ko: series.title || '',
          en: series.title_en || '',
        });
      }

      if (series.i18n_synopsis) {
        setI18nSynopsis(series.i18n_synopsis);
      } else {
        setI18nSynopsis({
          ko: series.synopsis || '',
          en: series.synopsis_en || '',
        });
      }

      if (series.i18n_seo_title) {
        setI18nSeoTitle(series.i18n_seo_title);
      }

      if (series.i18n_seo_synopsis) {
        setI18nSeoSynopsis(series.i18n_seo_synopsis);
      }

      if (series.i18n_seo_keyword) {
        setI18nSeoKeyword(series.i18n_seo_keyword);
      }

      // Enhanced Cast 데이터 초기화
      if (series.enhanced_cast) {
        const enhancedCastData = series.enhanced_cast.map((item: any) => ({
          cast: item.cast,
          type: item.type
        }));
        setEnhancedCast(enhancedCastData);
      }

      const seriesPeopleList = await peopleApiService.fetch(
        new URLSearchParams({
          page: '1',
          page_size: '100',
          series__id: series.id.toString(),
        })
      );
      if (seriesPeopleList) {
        setSelectedPeoples(seriesPeopleList.results.map((people: PeopleInterface) => people.id));
      }

      const extraPeopleList = await peopleApiService.fetch(
        new URLSearchParams({
          page: '1',
          page_size: '100',
        })
      );

      const combined = [...seriesPeopleList.results, ...extraPeopleList.results];

      const uniquePeople = combined.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

      setPeopleList(uniquePeople); // Select options으로 사용될 리스트
      setInitialPeopleList(uniquePeople); // 저장해두기
      setSearchedPeopleList(uniquePeople); // Enhanced Cast용
    };
    init();
  }, [form, series]);

  const searchPeople = async (value: string): Promise<void> => {
    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '100',
        search: value,
      })
    );
    setPeopleList(response.results);
  };

  // Enhanced Cast를 위한 people 검색 핸들러
  const searchPeopleForEnhancedCast = async (value: string): Promise<void> => {
    if (!value.trim()) return;

    const response = await peopleApiService.fetch(
      new URLSearchParams({
        page: '1',
        page_size: '20',
        search: value,
      })
    );
    setSearchedPeopleList(response.results);
  };

  return (
    <div>
      <div className="series-details-page">
        <h1>Series: {series?.title}</h1>
        <Form form={form} layout="vertical" className="series-details-form">
          <I18nFormFields
            fieldName="title"
            required={true}
            fieldType="input"
            value={i18nTitle}
            onChange={value => {
              setI18nTitle(value);
            }}
            disabled={readonly}
          />

          {/* 다국어 시놉시스 입력 */}
          <I18nFormFields
            fieldName="synopsis"
            required={false}
            fieldType="textarea"
            value={i18nSynopsis}
            onChange={value => {
              setI18nSynopsis(value);
            }}
            disabled={readonly}
          />

          <Form.Item
            label="Slug"
            name="slug"
            rules={[{ required: true, message: 'Please enter a slug' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="age_rating"
            label="Age Rating"
            initialValue={series?.age_rating}
            rules={[{ required: true, message: 'Please enter a age rating' }]}
          >
            <Select
              options={AGE_RATING.map(ageRating => ({ label: ageRating, value: ageRating }))}
            />
          </Form.Item>
          <Form.Item label="Status" name="status">
            <Select>
              {STATUS.map(status => (
                <Select.Option key={status} value={status}>
                  {status}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Dubbing Mode" name="dubbing_mode">
            <Select>
              {Object.entries(SERIES_DUBBING_MODE).map(([key, value]) => (
                <Select.Option key={key} value={key}>
                  {value}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="release_date_start"
            label="Release Start Date"
            initialValue={series?.release_date_start}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            name="release_date_end"
            label="Release End Date"
            initialValue={series?.release_date_end}
          >
            <DatePicker />
          </Form.Item>
          <Form.Item
            label="Produce Country"
            name="produce_country"
            initialValue={DEFAULT_PRODUCE_COUNTRY}
          >
            <Select
              value={series?.produce_country}
              style={{ width: 170 }}
              options={PRODUCE_COUNTRIES}
            ></Select>
          </Form.Item>
          {/* Enhanced Cast Editor */}
          <Form.Item label="Enhanced Cast">
            <EnhancedCastEditor
              value={enhancedCast}
              onChange={setEnhancedCast}
              peopleList={searchedPeopleList}
              onSearchPeople={searchPeopleForEnhancedCast}
            />
          </Form.Item>

          {/* SEO 정보 섹션 */}
          <Divider />
          <h3>SEO 정보</h3>

          {/* SEO 제목 */}
          <I18nFormFields
            fieldName="seo_title"
            required={false}
            fieldType="input"
            value={i18nSeoTitle}
            onChange={value => {
              setI18nSeoTitle(value);
              // 레거시 필드와 동기화
              form.setFieldsValue({
                seo_title: value.ko || '',
              });
            }}
            disabled={readonly}
          />

          {/* SEO 설명 */}
          <I18nFormFields
            fieldName="seo_synopsis"
            required={false}
            fieldType="textarea"
            value={i18nSeoSynopsis}
            onChange={value => {
              setI18nSeoSynopsis(value);
              // 레거시 필드와 동기화
              form.setFieldsValue({
                seo_synopsis: value.ko || '',
              });
            }}
            disabled={readonly}
          />

          {/* SEO 키워드 */}
          <I18nFormFields
            fieldName="seo_keyword"
            required={false}
            fieldType="input"
            value={i18nSeoKeyword}
            onChange={value => {
              setI18nSeoKeyword(value);
              // 레거시 필드와 동기화
              form.setFieldsValue({
                seo_keyword: value.ko || '',
              });
            }}
            disabled={readonly}
          />

          {/* 레거시 SEO 필드들 (숨김 처리) */}
          <Form.Item name="seo_title" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="seo_synopsis" style={{ display: 'none' }}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="seo_keyword" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
        </Form>
      </div>
      <div className="series-detail-update-button">
        <Component.ColorButtonComponent
          text={actionTitle}
          onClick={handleOnClickButtonClickWithI18n}
        />
      </div>
    </div>
  );
};

export default SeriesDetail;
